import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, TrendingDown, TrendingUp, AlertTriangle, BarChart3 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Alert, AlertDescription } from '../../ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import DocumentSelector from './DocumentSelector';
import { getStageData, saveStageData, completeStage, getClaimDetail } from '../../../api/claims';

const CalculationReviewModal = ({ open, onOpenChange, claimId, onComplete }) => {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  
  // Cost calculation state
  const [costPerUnit, setCostPerUnit] = useState('');
  const [showCostInput, setShowCostInput] = useState(false);

  // Fetch claim detail for financial data
  const { data: claimData, isLoading: claimLoading } = useQuery({
    queryKey: ['claimDetail', claimId],
    queryFn: () => getClaimDetail(claimId),
    enabled: open && !!claimId
  });

  // Fetch stage data
  const { data: stageDataResponse, isLoading: stageLoading } = useQuery({
    queryKey: ['stageData', claimId, 'calculation'],
    queryFn: () => getStageData(claimId, 'calculation'),
    enabled: open && !!claimId,
    onSuccess: (data) => {
      if (data?.data?.stage_data?.notes) {
        setNotes(data.data.stage_data.notes);
      }
    }
  });

  const claim = claimData?.data || {};
  const stageData = stageDataResponse?.data || {};

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data) => saveStageData(claimId, 'calculation', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['stageData', claimId, 'calculation']);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      setHasChanges(false);
    }
  });

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: () => completeStage(claimId, 'calculation'),
    onSuccess: () => {
      queryClient.invalidateQueries(['stageData', claimId]);
      queryClient.invalidateQueries(['claimDetail', claimId]);
      if (onComplete) onComplete();
      onOpenChange(false);
    }
  });

  const handleNotesChange = (value) => {
    setNotes(value);
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate({ notes });
  };

  const handleComplete = () => {
    if (hasChanges) {
      saveMutation.mutate({ notes }, {
        onSuccess: () => {
          completeMutation.mutate();
        }
      });
    } else {
      completeMutation.mutate();
    }
  };

  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US').format(num);
  };

  const isLoading = claimLoading || stageLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Calculation & Financial Review</span>
            {stageData.is_completed && (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            )}
          </DialogTitle>
          <DialogDescription>
            Review volume discrepancy, claim justification, and financial impact analysis.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Claim Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Claim Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Claim Number</Label>
                    <p className="font-medium">{claim.claim_number}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Claim Type</Label>
                    <p className="font-medium capitalize">{claim.claim_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">OEM</Label>
                    <p className="font-medium">{claim.opportunity_name}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Part Number</Label>
                    <p className="font-medium">{claim.part_number}</p>
                  </div>
                </div>
                {claim.notes && (
                  <div>
                    <Label className="text-sm text-gray-600">Claim Reason</Label>
                    <p className="text-sm mt-1">{claim.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Volume Discrepancy */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Volume Discrepancy Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Label className="text-xs text-blue-700 mb-1 block">Contracted Volume</Label>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatNumber(claim.contracted_volume)}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">Units</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <Label className="text-xs text-orange-700 mb-1 block">Actual Volume</Label>
                    <p className="text-2xl font-bold text-orange-900">
                      {formatNumber(claim.actual_volume)}
                    </p>
                    <p className="text-xs text-orange-600 mt-1">Units</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <Label className="text-xs text-red-700 mb-1 block">Shortage</Label>
                    <p className="text-2xl font-bold text-red-900">
                      {formatNumber(claim.volume_deviation)}
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      {claim.volume_deviation_pct}% deviation
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Impact: </span>
                    The OEM ordered <span className="font-semibold text-red-700">{formatNumber(claim.volume_deviation)} fewer units</span> than contracted, 
                    representing a <span className="font-semibold text-red-700">{claim.volume_deviation_pct}% shortfall</span> from the agreed volume of {formatNumber(claim.contracted_volume)} units.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Financial Impact */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Financial Impact & Cost Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Financial Impact */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <Label className="text-sm font-medium">Total Financial Impact</Label>
                    </div>
                    <p className="text-3xl font-bold text-red-600">
                      {formatCurrency(claim.financial_impact_cents)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Total claim value</p>
                  </div>

                  {/* Contracted Price */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Label className="text-sm font-medium">Contracted Price</Label>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">
                      {formatCurrency(claim.awarded_price_cents)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">Per unit</p>
                  </div>

                  {/* Estimated Cost */}
                  {claim.estimated_cost_cents > 0 && (
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="h-4 w-4 text-orange-600" />
                        <Label className="text-sm font-medium">Estimated Cost</Label>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(claim.estimated_cost_cents)}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">Total costs incurred</p>
                    </div>
                  )}

                  {/* Expected Compensation */}
                  {claim.expected_compensation_cents > 0 && (
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        <Label className="text-sm font-medium">Expected Compensation</Label>
                      </div>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(claim.expected_compensation_cents)}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">Target recovery</p>
                    </div>
                  )}
                </div>

                {/* Margins */}
                {(claim.action_margin > 0 || claim.contribution_margin > 0) && (
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    {claim.action_margin > 0 && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <Label className="text-sm font-medium">Action Margin</Label>
                        <Badge variant={claim.action_margin >= 50 ? "default" : "secondary"} className="text-base">
                          {claim.action_margin}%
                        </Badge>
                      </div>
                    )}
                    {claim.contribution_margin > 0 && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <Label className="text-sm font-medium">Contribution Margin</Label>
                        <Badge variant={claim.contribution_margin >= 30 ? "default" : "secondary"} className="text-base">
                          {claim.contribution_margin}%
                        </Badge>
                      </div>
                    )}
                  </div>
                )}

                {/* Calculation Summary */}
                <div className="p-3 bg-blue-50 rounded border border-blue-200 mt-4">
                  <p className="text-sm text-blue-900">
                    <span className="font-medium">Calculation Method: </span>
                    {claim.volume_deviation} units × {formatCurrency(claim.awarded_price_cents)} per unit = {formatCurrency(claim.financial_impact_cents)} total impact
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Cost Calculator */}
            {!stageData.is_completed && (!claim.estimated_cost_cents || !claim.action_margin) && (
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-orange-900">Calculate Production Costs & Margins</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      Enter your production cost per unit to automatically calculate estimated costs and profit margins.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="cost-per-unit" className="text-sm font-medium">
                          Your Cost Per Unit
                        </Label>
                        <div className="mt-1 relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            id="cost-per-unit"
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={costPerUnit}
                            onChange={(e) => {
                              setCostPerUnit(e.target.value);
                              setHasChanges(true);
                            }}
                            className="pl-7 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          What it costs you to produce one unit
                        </p>
                      </div>
                      
                      {costPerUnit && parseFloat(costPerUnit) > 0 && (
                        <div className="space-y-2">
                          <div className="p-3 bg-white rounded border">
                            <p className="text-xs text-gray-600">Estimated Total Cost</p>
                            <p className="text-lg font-bold text-gray-900">
                              {formatCurrency((parseFloat(costPerUnit) * 100) * (claim.volume_deviation || 0))}
                            </p>
                          </div>
                          <div className="p-3 bg-white rounded border">
                            <p className="text-xs text-gray-600">Profit Margin</p>
                            <p className="text-lg font-bold text-green-600">
                              {((((claim.awarded_price_cents / 100) - parseFloat(costPerUnit)) / (claim.awarded_price_cents / 100)) * 100).toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {costPerUnit && parseFloat(costPerUnit) > 0 && (
                      <Button
                        size="sm"
                        onClick={async () => {
                          const costCents = parseFloat(costPerUnit) * 100 * (claim.volume_deviation || 0);
                          const margin = ((claim.awarded_price_cents - (parseFloat(costPerUnit) * 100)) / claim.awarded_price_cents) * 100;
                          
                          try {
                            const { updateClaimPhase1Fields } = await import('../../../api/claims');
                            await updateClaimPhase1Fields(claimId, {
                              estimated_cost_cents: Math.round(costCents),
                              action_margin: parseFloat(margin.toFixed(2)),
                              contribution_margin: parseFloat((margin * 0.8).toFixed(2))
                            });
                            
                            queryClient.invalidateQueries(['claimDetail', claimId]);
                            setCostPerUnit('');
                            setHasChanges(false);
                          } catch (error) {
                            console.error('Failed to update costs:', error);
                            alert('Failed to save cost calculations');
                          }
                        }}
                      >
                        Apply Cost Calculations
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Additional Notes */}
            <div className="space-y-2">
              <Label htmlFor="calc-notes">Additional Calculation Notes & Methodology</Label>
              <Textarea
                id="calc-notes"
                value={notes}
                onChange={(e) => handleNotesChange(e.target.value)}
                placeholder="Add detailed cost breakdown, assumptions, methodology, risk factors, or any additional supporting calculations..."
                rows={4}
                disabled={stageData.is_completed}
              />
              <p className="text-xs text-gray-500">
                Document any additional assumptions, methodologies, or factors that support the calculation.
              </p>
            </div>

            {/* Supporting Documents */}
            <div className="space-y-2">
              <Label>Supporting Documents</Label>
              <DocumentSelector
                claimId={claimId}
                stageName="calculation"
                onDocumentAdded={() => setHasChanges(true)}
              />
            </div>

            {stageData.is_completed && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  This stage was completed on {new Date(stageData.completed_at).toLocaleDateString()}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {!stageData.is_completed && (
            <>
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saveMutation.isPending || !hasChanges}
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Draft'
                )}
              </Button>
              <Button
                onClick={handleComplete}
                disabled={completeMutation.isPending}
              >
                {completeMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark Complete
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CalculationReviewModal;
